// Chemin: C:\gestion-copro\src\services\gmail.service.ts
// Service Gmail - Copro Protect
// Session 44 : Ajout archiveEmailInGmail
// Session 45 : Ajout sendEmail pour repondre aux emails depuis l'app
// Session 49 : Enrichissement sendEmail avec tracabilite et support courriers

import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { functions, db } from './firebase';
import {
  GmailConnectionStatus,
  GmailConfig,
  EmailHeader,
  EmailDetail,
  EmailListResponse,
  EmailSearchParams,
  EmailAttachment,
} from '../types/gmail.types';

// ============================================================================
// AUTHENTIFICATION GMAIL
// ============================================================================

/**
 * Recupere l'URL d'autorisation Google OAuth
 */
export const getGmailAuthUrl = async (coproprieteId: string): Promise<string> => {
  const getAuthUrl = httpsCallable<{ coproprieteId: string }, { authUrl: string }>(
    functions,
    'getGmailAuthUrl'
  );

  const result = await getAuthUrl({ coproprieteId });
  return result.data.authUrl;
};

/**
 * Echange le code OAuth contre des tokens
 */
export const handleGmailCallback = async (
  code: string,
  coproprieteId: string
): Promise<{ success: boolean; email?: string; error?: string }> => {
  const callback = httpsCallable<
    { code: string; coproprieteId: string },
    { success: boolean; email?: string; error?: string }
  >(functions, 'handleGmailCallback');

  const result = await callback({ code, coproprieteId });
  return result.data;
};

/**
 * Deconnecte Gmail de la copropriete
 */
export const disconnectGmail = async (coproprieteId: string): Promise<void> => {
  const disconnect = httpsCallable<{ coproprieteId: string }, { success: boolean }>(
    functions,
    'disconnectGmail'
  );

  await disconnect({ coproprieteId });
};

/**
 * Initie le flux OAuth Gmail
 * Ouvre une nouvelle fenetre vers Google
 */
export const initiateGmailAuth = async (coproprieteId: string): Promise<void> => {
  const authUrl = await getGmailAuthUrl(coproprieteId);
  
  // Stocker l'ID de la copropriete pour le callback
  sessionStorage.setItem('gmail_auth_copropriete_id', coproprieteId);
  
  // Rediriger vers Google
  window.location.href = authUrl;
};

/**
 * Ecoute en temps reel le statut de connexion Gmail
 * Fix session 242 : mémorisation du dernier état émis — le doc copropriete reçoit
 * de nombreuses écritures (quotas, sentinelleUsage, etc.) qui déclenchaient le listener
 * en boucle et provoquaient une cascade de fetchEmails 500.
 */
export const subscribeToGmailStatus = (
  coproprieteId: string,
  callback: (status: GmailConnectionStatus) => void
): (() => void) => {
  const docRef = doc(db, 'coproprietes', coproprieteId);

  // Mémoriser le dernier état émis pour éviter les notifications redondantes
  let lastIsConnected: boolean | null = null;
  let lastEmail: string | undefined = undefined;

  return onSnapshot(
    docRef,
    (snapshot) => {
      let newIsConnected = false;
      let newEmail: string | undefined = undefined;
      let connectedAt: Date = new Date();

      if (snapshot.exists()) {
        const data = snapshot.data();
        const gmailConfig = data?.gmailConfig as GmailConfig | undefined;

        if (gmailConfig && gmailConfig.isActive) {
          newIsConnected = true;
          newEmail = gmailConfig.email;
          const connectedAtRaw = gmailConfig.connectedAt as unknown;
          connectedAt = connectedAtRaw && typeof (connectedAtRaw as { toDate?: () => Date }).toDate === 'function'
            ? (connectedAtRaw as { toDate: () => Date }).toDate()
            : new Date();
        }
      }

      // N'émettre que si l'état a réellement changé
      if (newIsConnected === lastIsConnected && newEmail === lastEmail) return;

      lastIsConnected = newIsConnected;
      lastEmail = newEmail;

      if (newIsConnected) {
        callback({ isConnected: true, email: newEmail, connectedAt });
      } else {
        callback({ isConnected: false });
      }
    },
    (error) => {
      console.error('Erreur ecoute statut Gmail:', error);
      callback({ isConnected: false, error: error.message });
    }
  );
};

// ============================================================================
// RECUPERATION DES EMAILS
// ============================================================================

/**
 * Recupere la liste des emails
 */
export const fetchEmails = async (
  coproprieteId: string,
  params?: EmailSearchParams
): Promise<EmailListResponse> => {
  const fetch = httpsCallable<
    { coproprieteId: string; params?: EmailSearchParams },
    EmailListResponse
  >(functions, 'fetchEmails');

  const result = await fetch({ coproprieteId, params });
  
  // Convertir les dates string en Date
  const emails: EmailHeader[] = result.data.emails.map((email) => ({
    ...email,
    date: new Date(email.date as unknown as string),
  }));

  return {
    ...result.data,
    emails,
  };
};

/**
 * Recupere le detail complet d'un email
 */
export const fetchEmailDetail = async (
  coproprieteId: string,
  emailId: string
): Promise<EmailDetail> => {
  const fetch = httpsCallable<
    { coproprieteId: string; emailId: string },
    EmailDetail
  >(functions, 'fetchEmailDetail');

  const result = await fetch({ coproprieteId, emailId });
  
  return {
    ...result.data,
    date: new Date(result.data.date as unknown as string),
  };
};

/**
 * Telecharge une piece jointe
 */
export const fetchAttachment = async (
  coproprieteId: string,
  emailId: string,
  attachmentId: string
): Promise<{ data: string; size: number }> => {
  const fetch = httpsCallable<
    { coproprieteId: string; emailId: string; attachmentId: string },
    { data: string; size: number }
  >(functions, 'fetchAttachment');

  const result = await fetch({ coproprieteId, emailId, attachmentId });
  return result.data;
};

/**
 * Telecharge et ouvre une piece jointe dans un nouvel onglet
 */
export const downloadAttachment = async (
  coproprieteId: string,
  emailId: string,
  attachment: EmailAttachment
): Promise<void> => {
  const result = await fetchAttachment(coproprieteId, emailId, attachment.id);
  
  // Convertir base64 en blob
  const byteCharacters = atob(result.data.replace(/-/g, '+').replace(/_/g, '/'));
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: attachment.mimeType });
  
  // Creer un lien de telechargement
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = attachment.filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ============================================================================
// ARCHIVAGE GMAIL
// ============================================================================

/**
 * Archive un email dans Gmail :
 * - Ajoute le label "Copro-Protect/Archive"
 * - Retire de la boite de reception
 */
export const archiveEmailInGmail = async (
  coproprieteId: string,
  emailId: string
): Promise<{ success: boolean; message: string }> => {
  const archive = httpsCallable<
    { coproprieteId: string; emailId: string },
    { success: boolean; message: string; labelId: string }
  >(functions, 'archiveEmailInGmail');

  const result = await archive({ coproprieteId, emailId });
  return {
    success: result.data.success,
    message: result.data.message,
  };
};

/**
 * Met un email dans la corbeille Gmail
 * L'email reste recuperable pendant 30 jours
 */
export const trashEmail = async (
  coproprieteId: string,
  emailId: string
): Promise<{ success: boolean; message: string }> => {
  const trash = httpsCallable<
    { coproprieteId: string; emailId: string },
    { success: boolean; message: string }
  >(functions, 'trashEmail');

  const result = await trash({ coproprieteId, emailId });
  return result.data;
};

// ============================================================================
// ENVOI D'EMAIL
// ============================================================================

/**
 * Parametres pour l'envoi d'un email
 */
export interface SendEmailParams {
  to: string;
  subject: string;
  body: string;
  isHtml?: boolean;
  // Tracabilite (obligatoire)
  userId: string;
  userNom: string;
  // Lien vers courrier (optionnel - si envoi depuis module Courriers)
  courrierId?: string;
  // Pour les reponses / threading (optionnel)
  threadId?: string;
  inReplyTo?: string;
  references?: string;
}

/**
 * Reponse de l'envoi d'email
 */
export interface SendEmailResponse {
  success: boolean;
  messageId?: string;
  threadId?: string;
  error?: string;
}

/**
 * Envoie un email via Gmail API
 * - Tracabilite complete dans les logs
 * - Si courrierId fourni, met a jour le statut du courrier
 * - Si threadId/inReplyTo fournis, l'email sera threade avec l'original
 */
export const sendEmail = async (
  coproprieteId: string,
  params: SendEmailParams
): Promise<SendEmailResponse> => {
  const send = httpsCallable<
    { coproprieteId: string } & SendEmailParams,
    SendEmailResponse
  >(functions, 'sendEmail');

  const result = await send({
    coproprieteId,
    ...params,
  });

  return result.data;
};

/**
 * Prepare les parametres de reponse a partir d'un email existant
 * Gere automatiquement le sujet (ajout "Re:" si necessaire) et les headers de threading
 */
export const prepareReplyParams = (
  originalEmail: {
    id: string;
    threadId: string;
    fromEmail: string;
    subject: string;
  }
): Partial<SendEmailParams> => {
  // Ajouter "Re:" au sujet s'il n'est pas deja present
  const subject = originalEmail.subject.toLowerCase().startsWith('re:')
    ? originalEmail.subject
    : `Re: ${originalEmail.subject}`;

  // Le Message-ID Gmail est generalement au format <id@mail.gmail.com>
  const messageId = `<${originalEmail.id}@mail.gmail.com>`;

  return {
    to: originalEmail.fromEmail,
    subject,
    threadId: originalEmail.threadId,
    inReplyTo: messageId,
    references: messageId,
  };
};

// ============================================================================
// UTILITAIRES
// ============================================================================

/**
 * Formate une date d'email pour l'affichage
 */
export const formatEmailDate = (date: Date): string => {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const diffDays = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    // Aujourd'hui : afficher l'heure
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } else if (diffDays === 1) {
    return 'Hier';
  } else if (diffDays < 7) {
    // Cette semaine : afficher le jour
    return date.toLocaleDateString('fr-FR', { weekday: 'long' });
  } else {
    // Plus ancien : afficher la date
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  }
};

/**
 * Tronque le snippet d'un email
 */
export const truncateSnippet = (snippet: string, maxLength: number = 100): string => {
  if (snippet.length <= maxLength) return snippet;
  return snippet.substring(0, maxLength) + '...';
};